import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Button, Alert, CircularProgress,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import MessageIcon from '@mui/icons-material/Message';
import SchoolIcon from '@mui/icons-material/School';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HistoryIcon from '@mui/icons-material/History';
import BugReportIcon from '@mui/icons-material/BugReport';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { apiFetch } from '../apiFetch';
import TwoFactorSetupModal from '../modals/TwoFactorSetupModal';

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  // Auth state
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Admin 2FA state
  const [requiresSetup, setRequiresSetup] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [devBypass, setDevBypass] = useState(false);
  const [setupModalOpen, setSetupModalOpen] = useState(false);

  // 2FA verify dialog state
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  // Admin session
  const [adminToken, setAdminToken] = useState<string | null>(() => {
    return sessionStorage.getItem('admin_token');
  });

  useEffect(() => {
    checkAuthorization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthorization = async () => {
    setLoading(true);

    // First check if user is logged in and is staff/superuser
    const token = localStorage.getItem('access_token');
    if (!token) {
      setIsAuthorized(false);
      setLoading(false);
      return;
    }

    try {
      // Check admin 2FA status
      const data = await apiFetch(`${apiUrl}/api/admin/2fa/status/`);

      if (data.error) {
        setIsAuthorized(false);
        setLoading(false);
        return;
      }

      setIsAuthorized(true);
      setDevBypass(data.dev_bypass || false);

      if (data.dev_bypass) {
        // Dev server - get admin token directly
        await getAdminToken();
      } else if (data.requires_setup) {
        setRequiresSetup(true);
      } else if (!adminToken) {
        setRequires2FA(true);
        setVerifyDialogOpen(true);
      }
    } catch (err: unknown) {
      const error = err as Error;
      if (error.message?.includes('Not authorized') || error.message?.includes('403')) {
        setIsAuthorized(false);
      } else {
        setError(error.message || 'Failed to check admin status');
      }
    } finally {
      setLoading(false);
    }
  };

  const getAdminToken = async (code?: string) => {
    try {
      const body: { code?: string } = {};
      if (code) body.code = code;

      const data = await apiFetch(`${apiUrl}/api/admin/2fa/verify/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      sessionStorage.setItem('admin_token', data.admin_token);
      setAdminToken(data.admin_token);
      setRequires2FA(false);
      setVerifyDialogOpen(false);
    } catch (err: unknown) {
      const error = err as Error;
      throw error;
    }
  };

  const handleVerify2FA = async () => {
    if (verifyCode.length !== 6) {
      setVerifyError('Please enter a 6-digit code');
      return;
    }

    setVerifying(true);
    setVerifyError(null);

    try {
      await getAdminToken(verifyCode);
    } catch (err: unknown) {
      const error = err as Error;
      setVerifyError(error.message || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleSetupSuccess = async () => {
    setSetupModalOpen(false);
    setRequiresSetup(false);
    setRequires2FA(true);
    setVerifyDialogOpen(true);
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 5, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Not authorized - show 404
  if (isAuthorized === false) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Typography variant="h4" gutterBottom>404 - Page Not Found</Typography>
        <Typography variant="body1">The page you are looking for does not exist.</Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Go Home
        </Button>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Container>
    );
  }

  // Requires 2FA setup
  if (requiresSetup) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Box className="page-header">
          <Box className="title-row">
            <IconButton onClick={() => navigate('/')} aria-label="back to home">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" className="page-title">Admin Setup</Typography>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Admin two-factor authentication is required to access this section.
          Please set up 2FA to continue.
        </Alert>

        <Button
          variant="contained"
          onClick={() => setSetupModalOpen(true)}
          startIcon={<LockIcon />}
        >
          Set Up Admin 2FA
        </Button>

        <TwoFactorSetupModal
          open={setupModalOpen}
          onClose={() => setSetupModalOpen(false)}
          onSuccess={handleSetupSuccess}
          purpose="admin"
        />
      </Container>
    );
  }

  // Requires 2FA verification (no valid session)
  if (requires2FA && !adminToken) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Box className="page-header">
          <Box className="title-row">
            <IconButton onClick={() => navigate('/')} aria-label="back to home">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" className="page-title">Admin Access</Typography>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Please verify your admin identity to continue.
        </Alert>

        <Dialog open={verifyDialogOpen} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockIcon color="primary" />
            Admin 2FA Verification
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the 6-digit code from your authenticator app.
            </Typography>
            {verifyError && (
              <Alert severity="error" sx={{ mb: 2 }}>{verifyError}</Alert>
            )}
            <TextField
              label="Authentication Code"
              variant="outlined"
              fullWidth
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              inputProps={{
                maxLength: 6,
                inputMode: 'numeric',
                pattern: '[0-9]*',
                style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.5rem' },
              }}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && verifyCode.length === 6) {
                  handleVerify2FA();
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => navigate('/')} disabled={verifying}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleVerify2FA}
              disabled={verifying || verifyCode.length !== 6}
            >
              {verifying ? <CircularProgress size={24} /> : 'Verify'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  // Authorized with valid session - show admin dashboard
  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <Box className="page-header">
        <Box className="title-row">
          <IconButton onClick={() => navigate('/')} aria-label="back to home">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="page-title">Admin Console</Typography>
        </Box>
      </Box>

      {devBypass && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Development mode: 2FA verification is bypassed.
        </Alert>
      )}

      <Typography variant="body1" sx={{ mb: 3 }}>
        Welcome to the admin console. Select an option below to manage the application.
      </Typography>

      <List>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/admin/users">
            <ListItemIcon>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText
              primary="User Management"
              secondary="View, search, and manage user accounts"
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/admin/settings">
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Site Settings"
              secondary="Configure application-wide settings"
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/admin/email-hub">
            <ListItemIcon>
              <EmailIcon />
            </ListItemIcon>
            <ListItemText
              primary="Email Hub"
              secondary="Create and edit email templates and content"
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/admin/messages">
            <ListItemIcon>
              <MessageIcon />
            </ListItemIcon>
            <ListItemText
              primary="View Messages"
              secondary="Contact form submissions and mailing list signups"
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/admin/error-tracking">
            <ListItemIcon>
              <BugReportIcon />
            </ListItemIcon>
            <ListItemText
              primary="Error Tracking"
              secondary="Monitor application errors and alerts"
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/admin/analytics">
            <ListItemIcon>
              <AnalyticsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Analytics"
              secondary="Site performance and business metrics"
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/admin/audit-trails">
            <ListItemIcon>
              <HistoryIcon />
            </ListItemIcon>
            <ListItemText
              primary="Audit Trails"
              secondary="Track user activity for customer service"
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/admin/tutorials">
            <ListItemIcon>
              <SchoolIcon />
            </ListItemIcon>
            <ListItemText
              primary="Tutorials & Videos"
              secondary="Manage YouTube videos and tutorials"
            />
          </ListItemButton>
        </ListItem>
        {/* Under construction sections - lighter styling */}
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/admin/payments" sx={{ opacity: 0.5 }}>
            <ListItemIcon sx={{ opacity: 0.6 }}>
              <PaymentIcon />
            </ListItemIcon>
            <ListItemText
              primary="Payments"
              secondary="User payments, refunds, and billing management"
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/admin/promotions" sx={{ opacity: 0.5 }}>
            <ListItemIcon sx={{ opacity: 0.6 }}>
              <LocalOfferIcon />
            </ListItemIcon>
            <ListItemText
              primary="Promotions & Coupons"
              secondary="Discounts, promotional codes, and marketing offers"
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Container>
  );
};

export default Admin;
